import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, } from 'react-native';
import { ModalFlatList } from 'design-system/ModalScrollables';
import { openShare } from 'utils/packages/rnShare';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import useColors from 'hooks/useColors';
import { selectDefaultCurrency } from 'reducers/selectors';
import { Button, Spacer } from 'design-system';
import { IconBell } from 'design-system/icons';
import { HEIGHT } from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Modal from 'utils/packages/Modal';
import IconSend from 'design-system/icons/IconSend';
import { navigationRef } from 'utils/navigationv6';
import { getGroupById } from 'actions';
import { muteErrorAndResult, useFetchOld, useScrollableModalMaxHeight, } from 'hooks';
import { mapBreakdownToPaymentActivity } from '../types/types';
import PaymentItem from './PaymentItem';
import { getCreditAndDebt, sendReminders, } from '../actions/payments';
import { selectCreditAndDebtBreakdown } from '../selectors';
import { createRequestMessage } from '../util/createMessage';
import { useRemindedRequest } from '../hooks';
const strings = {
    creditSubtitle: 'Nudge your friends by sending a reminder',
    creditBtn: 'Remind',
    debtSubtitle: 'Select a friend to settle your debt',
    debtBtn: 'Pay',
};
export const breakdownKeyExtractor = (item) => {
    if (item.paymentRequest) {
        return item.paymentRequest.id.toString();
    }
    if (item.spendingGroup && 'handle' in item.counterparty) {
        return `${item.spendingGroup.id}${item.counterparty.handle}`;
    }
    return `${'nickname' in item.counterparty ? item.counterparty.nickname : ''}${item.amount}`;
};
export const CreditAndDebtModalContent = ({ flow, onScroll, }) => {
    const isCredit = flow === 'credit';
    const currency = useAppSelector(selectDefaultCurrency);
    const { creditBreakdown, debtBreakdown } = useAppSelector(selectCreditAndDebtBreakdown);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getCreditAndDebt());
    }, []);
    const [selected, setSelected] = useState([]);
    const onSettleGroup = useCallback((counterparty, spendingGroup, amount) => {
        Modal.hide(() => navigationRef.navigateDeprecated('PayEnterAmount', {
            to: {
                contact: {
                    givenName: counterparty.firstName,
                    familyName: counterparty.lastName,
                    thumbnailPath: counterparty.imageUrl,
                    handle: counterparty.handle,
                    phoneNumbers: [],
                },
                itemType: 'pay',
            },
            onPaymentMadeCallback: {
                callbackType: 'SPENDING_GROUP_SETTLEMENT',
                groupId: spendingGroup.id,
                payeeUserHandle: counterparty.handle,
            },
            initialAmount: amount,
            initialReference: 'Debt settlement',
            onFinishPayment: () => {
                dispatch(getGroupById(spendingGroup.id));
                dispatch(getCreditAndDebt());
            },
        }));
    }, []);
    const onSettleRequest = useCallback((payment) => {
        Modal.hide(() => navigationRef.navigateDeprecated('PaymentActivity', { payment }));
    }, []);
    const [, , isReminding, fetch] = useFetchOld(muteErrorAndResult);
    const [, setReminded] = useRemindedRequest();
    const onSendReminders = useCallback(async () => {
        const body = {};
        const remindedCounterparties = [];
        selected.forEach((s) => {
            if (!('handle' in s.counterparty && s.counterparty.handle))
                return;
            const alreadyInList = remindedCounterparties.find((reminded) => 'handle' in reminded &&
                'handle' in s.counterparty &&
                reminded.handle === s.counterparty.handle);
            if (!alreadyInList) {
                remindedCounterparties.push(s.counterparty);
            }
            if (s.spendingGroup) {
                if (!body.groupSettlements) {
                    body.groupSettlements = [];
                }
                body.groupSettlements.push({
                    groupId: s.spendingGroup.id,
                    payerUserHandle: s.counterparty.handle,
                });
            }
            if (s.paymentRequest) {
                if (!body.paymentRequests) {
                    body.paymentRequests = [];
                }
                body.paymentRequests.push({
                    paymentRequestId: s.paymentRequest.id,
                    payerUserHandle: s.counterparty.handle,
                });
            }
        });
        const res = await fetch(sendReminders(body));
        if (!res)
            return;
        Modal.hide(() => setReminded(remindedCounterparties));
    }, [selected, setReminded]);
    const onPress = useCallback(() => {
        if (!selected.length)
            return;
        if (isCredit) {
            onSendReminders();
        }
        else {
            const { counterparty, spendingGroup, paymentRequest, amount } = selected[0];
            if ('handle' in counterparty && spendingGroup) {
                onSettleGroup(counterparty, spendingGroup, amount);
            }
            if ('handle' in counterparty && paymentRequest) {
                onSettleRequest(mapBreakdownToPaymentActivity(selected[0], currency, isCredit, breakdownKeyExtractor(selected[0])));
            }
        }
    }, [
        currency,
        isCredit,
        onSendReminders,
        onSettleGroup,
        onSettleRequest,
        selected,
    ]);
    const onSelect = useCallback((item, isCredit) => {
        if (!('handle' in item.counterparty && item.counterparty.handle) &&
            item.paymentRequest) {
            const shareMessage = createRequestMessage(item.amount, item.paymentRequest?.link, item.paymentRequest?.message);
            Modal.show(<>
          <Spacer.H8 />
          <Text TextThin-14 Secondary centered>
            Looks like they haven’t joined Emma. Share the link and get paid
          </Text>
          <Spacer h={rem(32)}/>
          <Button brand title="Share link to get paid" leftIcon={<IconSend colorKey="white"/>} onPress={() => openShare({ message: shareMessage })}/>
        </>, {
                title: 'Friend not on Emma',
                top: true,
            });
            return;
        }
        setSelected((prev) => {
            const index = prev.findIndex((s) => breakdownKeyExtractor(s) === breakdownKeyExtractor(item));
            const isSelected = index !== -1;
            if (isSelected) {
                return prev.filter((_, i) => i !== index);
            }
            if (isCredit) {
                return [...prev, item];
            }
            return [item];
        });
    }, []);
    const ListHeaderComponent = useMemo(() => (<Text TextThin-14 Secondary centered style={styles.text}>
        {isCredit ? strings.creditSubtitle : strings.debtSubtitle}
      </Text>), [isCredit]);
    const data = useMemo(() => (isCredit ? creditBreakdown : debtBreakdown), [creditBreakdown, debtBreakdown, isCredit]);
    const colors = useColors();
    const renderItem = useCallback(({ item }) => {
        const isSelected = !!selected.find((s) => breakdownKeyExtractor(s) === breakdownKeyExtractor(item));
        const disabled = !('handle' in item.counterparty && item.counterparty.handle) &&
            !!item.paymentRequest;
        const payment = mapBreakdownToPaymentActivity(item, currency, isCredit, breakdownKeyExtractor(item));
        return (<TouchableHighlight onCard hoverOnLight activeOpacity={1} onPress={() => onSelect(item, isCredit)} underlayColor={colors.background.underlay}>
          <PaymentItem payment={payment} isSelectable isMultiSelect={isCredit} hideActions spendingGroup={item.spendingGroup} isSelected={isSelected} disabled={disabled}/>
        </TouchableHighlight>);
    }, [colors.background.underlay, currency, isCredit, onSelect, selected]);
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(8));
    return (<>
      <ModalFlatList data={data} onScroll={onScroll} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} renderItem={renderItem} ListHeaderComponent={ListHeaderComponent} style={style} contentContainerStyle={contentContainerStyle}/>
      <Button brand floating title={isCredit ? strings.creditBtn : strings.debtBtn} leftIcon={isCredit ? <IconBell colorKey="white"/> : undefined} disabled={!selected.length} onPress={onPress} containerStyle={styles.button} isFetching={isReminding}/>
    </>);
};
const styles = StyleSheet.create({
    button: {
        marginHorizontal: 0,
    },
    text: {
        paddingBottom: rem(8),
    },
});
